/**
 * React hook for managing company users data
 * 
 * This hook provides a convenient way to fetch and access company users
 * with automatic caching and error handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  getCompanyMembers,
  getUserCompanies,
  getAllUserProfiles,
  subscribeToUserData
} from './stores/unifiedUserStore';
import { UserProfile } from './stores/storeTypes';
import { useToast } from '@/hooks/use-toast';

interface UseCompanyUsersOptions {
  companyId?: string;
  autoFetch?: boolean;
}

interface UseCompanyUsersReturn {
  users: UserProfile[];
  loading: boolean;
  error: string | null;
  fetchUsers: (companyId: string) => Promise<void>;
  getUserById: (userId: string) => UserProfile | null;
  getUsersByRole: (role: string) => UserProfile[];
  refresh: () => Promise<void>;
}

/**
 * Hook to manage company users data
 * @param options - Configuration options
 * @returns Object with users data and utility functions
 */
export function useCompanyUsers(options: UseCompanyUsersOptions = {}): UseCompanyUsersReturn {
  const { companyId, autoFetch = false } = options;
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch users for a specific company from unified store
  const fetchUsers = useCallback(async (targetCompanyId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Get company members from unified store
      const companyMembers = getCompanyMembers(targetCompanyId);
      setUsers(companyMembers);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get cached users for the current company
  const getCachedUsers = useCallback(() => {
    if (!companyId) return [];
    return getCompanyMembers(companyId);
  }, [companyId]);

  // Get a specific user by ID
  const getUserById = useCallback((userId: string): UserProfile | null => {
    const allUsers = getCachedUsers();
    return allUsers.find(user => user.id === userId) || null;
  }, [getCachedUsers]);

  // Get users by role for the current company - Note: role filtering not implemented in unified store yet
  const getUsersByRole = useCallback((role: string): UserProfile[] => {
    if (!companyId) return [];
    // TODO: Add role filtering to unified store
    return getCachedUsers();
  }, [companyId, getCachedUsers]);

  // Refresh users data
  const refresh = useCallback(async () => {
    if (companyId) {
      await fetchUsers(companyId);
    }
  }, [companyId, fetchUsers]);

  // Auto-fetch users when companyId changes and autoFetch is enabled
  useEffect(() => {
    if (autoFetch && companyId) {
      // First try to get cached data
      const cachedUsers = getCompanyMembers(companyId);
      if (cachedUsers.length > 0) {
        setUsers(cachedUsers);
      }
      
      // Then fetch fresh data
      fetchUsers(companyId);
    }
  }, [companyId, autoFetch, fetchUsers]);

  // Initialize with cached data if available
  useEffect(() => {
    if (companyId) {
      const cachedUsers = getCompanyMembers(companyId);
      if (cachedUsers.length > 0) {
        setUsers(cachedUsers);
      }
    }
  }, [companyId]);

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = subscribeToUserData(() => {
      if (companyId) {
        const updatedUsers = getCompanyMembers(companyId);
        setUsers(updatedUsers);
      }
    });
    
    return unsubscribe;
  }, [companyId]);

  return {
    users,
    loading,
    error,
    fetchUsers,
    getUserById,
    getUsersByRole,
    refresh,
  };
} 